import Hero from '@components/hero/Hero';
import HubSpotForm from '@components/hubspot/HubSpotForm';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import HeartBubble from '@ui/icons/heart-bubble.svg';
import { makeSeoData } from 'config/next-seo.config';
import Head from 'next/head';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import React from 'react';

import useStyles from './Sell.style';

const title = 'Sell Art & Goods on Guest House | Home Staging You Can Shop';
const description = 'Sell your art and goods on Guest House, and you\'ll receive beautiful photography, support, and showroom space completely free. Learn more. ';
const image = '/images/sell/brand-exposure-background.jpg';

function Make() {
  const { classes, theme } = useStyles();
  const { sm, md, lg } = useBreakpoints();


  return (
    <>
      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description, image })} />

      <ContentBlock
        maxWidth={1365}
        margin={[6, 0, 4]}
        marginMd={[12, 0, 7]}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={7}
          >
            <Typography
              variant="h1"
              className={classes.h1}
            >
              Join our native retail ecosystem
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              className={classes.subhead}
              style={{ marginTop: theme.spacing(3) }}
            >
              Reach consumers when and where they are ready to shop – their new home – allowing them to experience your brand in a completely new, authentic, and engaging way.
              {' '}
            </Typography>
          </Grid>
        </Grid>
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
        margin={[4, 0]}
        marginMd={[7, 0]}
      >
        <div
          style={{
            borderRadius: theme.shape.borderRadius,
            overflow: 'hidden',
            position: 'relative',
            height: sm ? 520 : 215,
            width: '100%',
          }}
        >
          <Image
            fill
            src="/images/sell/brand-page-photo.png"
            alt=""
            sizes="100vw"
            style={{
              objectFit: 'cover'
            }}
          />
        </div>
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
        margin={[4, 0]}
        marginMd={[7, 0]}
      >
        <Typography
          variant="h2"
          align="center"
          style={{ marginBottom: '2em' }}
        >
          Why work with us?
        </Typography>
        <Grid
          container
          spacing={2}
          style={{ marginTop: theme.spacing(4) }}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <div
              style={{
                borderRadius: theme.shape.borderRadius,
                overflow: 'hidden',
                position: 'relative',
                height: sm ? 411 : 246,
                width: '100%',
              }}
            >
              <Image
                fill
                src="/images/sell/free-premium-content.jpg"
                alt=""
                sizes="100vw"
                style={{
                  objectFit: 'cover'
                }}
              />
            </div>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Box
              width="100%"
              height="100%"
              display="flex"
              justifyContent="center"
              flexDirection="column"
              style={{ padding: md ? '0% 15%' : 0 }}
            >
              <Typography
                variant="h3Alt"
                style={{ marginBottom: '.5em', marginTop: md ? 0 : theme.spacing(2) }}
              >
                Open houses are the new showrooms
              </Typography>
              <Typography style={{ color: theme.palette.common.black }}>
                Create truly authentic and intimate retail experiences by placing your brand directly in beautiful properties on the market. Potential buyers can see your products in native spaces and start a relationship with your brand through in-room signage and clear calls to action.
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <div style={{ margin: theme.spacing(10, 0) }}>
          <Hero
            backgroundImage="/images/sell/brand-exposure-background.jpg"
            style={{
              backgroundPosition: 'center',
            }}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              maxWidth={400}
            >
              <HeartBubble style={{ marginBottom: theme.spacing(2), width: theme.spacing(3), height: 'auto' }} />
              <Typography
                variant="h3Alt"
                style={{ marginBottom: '.5em', color: theme.palette.common.white }}
                align="center"
              >
                Unlock a fresh audience ready to spend
              </Typography>
              <Typography
                style={{ color: theme.palette.common.white }}
                align="center"
              >
                Every home staged has access to hundreds of potential buyers that get to see your products in person and an audience of thousands online.
              </Typography>
            </Box>
          </Hero>
        </div>

        <Grid
          container
          spacing={2}
          style={{ marginTop: theme.spacing(4) }}
        >
          <Grid
            item
            xs={12}
            md={6}
            style={{
              order: md ? 0 : 1,
            }}
          >
            <Box
              width="100%"
              height="100%"
              display="flex"
              justifyContent="center"
              flexDirection="column"
              style={{ padding: md ? '0% 15%' : 0 }}
            >
              <Typography
                variant="h3Alt"
                style={{ marginBottom: '.5em', marginTop: md ? 0 : theme.spacing(2) }}
              >
                Premium editorial content
              </Typography>
              <Typography style={{ color: theme.palette.common.black }}>
                By partnering with Guest House, you’ll receive professional images of your products styled in our homes for your own marketing and promotional use. Our photographers will include various editorial angles and shots of your pieces in the best light.
                {' '}
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            style={{
              order: md ? 1 : 0,
            }}
          >
            <div style={{ position: 'relative', paddingLeft: lg ? 100 : md ? 75 : 50 }}>
              <div
                style={{
                  borderRadius: theme.shape.borderRadius,
                  overflow: 'hidden',
                  maxWidth: 200,
                  position: 'absolute',
                  zIndex: 2,
                  left: 0,
                  top: '50%',
                  transform: 'translate(0, -50%)',
                  letterSpacing: 0,
                  wordSpacing: 0,
                  fontSize: 0,
                }}
              >
                <Image
                  src="/images/sell/instagram-screen.jpg"
                  alt=""
                  width={lg ? 221 : md ? 178 : 135}
                  height={lg ? 372 : md ? 300 : 228}
                  style={{
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                />

              </div>
              <div
                style={{
                  borderRadius: theme.shape.borderRadius,
                  overflow: 'hidden',
                  position: 'relative',
                  height: sm ? 411 : 246,
                  width: '100%',
                }}
              >
                <Image
                  fill
                  src="/images/sell/marketing-and-sales.jpg"
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'cover'
                  }}
                />
              </div>
            </div>

          </Grid>
        </Grid>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        style={{ backgroundColor: md ? '' : '#e6e6e6', }}
        padding={[12, 0]}
        margin={0}
      >
        <ContentBlock
          maxWidth={1365}
          margin={[0, 'auto']}
          style={{
            backgroundColor: md ? '#e6e6e6' : '',
            borderRadius: md ? theme.shape.borderRadius : '',
            padding: md ? theme.spacing(8, 10) : '',

          }}
        >
          <Grid container>
            <Grid
              item
              xs={12}
              md={12}
            >
              <Typography
                variant="h2"
                style={{ marginBottom: theme.spacing(3) }}
              >
                <a id="get-started">
                  Request information
                </a>
              </Typography>
            </Grid>
          </Grid>

          <div style={{ margin: theme.spacing(5, 0) }}>
            <HubSpotForm
              portalId="8563089"
              formId="96d625ea-98b3-4c36-8d87-ec7915a1bfaf"
              onFormSubmitted={() => {
                window.analytics?.track('maker_application_submitted');
              }}
            />
          </div>
        </ContentBlock>
      </ContentBlock>
    </>
  );
}

export default Make;
